'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { nl } from 'react-day-picker/locale';

import { Label } from '@components/Common';
import { ErrorLabel } from '@components/Input/styles';

import {
  CalendarDropdown,
  DateInputContainer,
  DateInputWrapper,
  DateTrigger,
  IconWrapper,
  Overlay,
} from './styles';

import 'react-day-picker/style.css';

interface DateInputProps {
  id: string;
  name: string;
  label?: string;
  placeholder?: string;
  value?: string;
  hasError?: boolean;
  errorLabel?: string;
  icon?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const formatDisplayDate = (isoDate: string): string => {
  const [year, month, day] = isoDate.split('-');
  if (!year || !month || !day) return '';
  return `${day}-${month}-${year}`;
};

const toISODate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseISODate = (isoDate: string): Date | undefined => {
  if (!isoDate) return undefined;
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
};

export const DateInput: React.FC<DateInputProps> = ({
  id,
  name,
  label,
  placeholder = 'dd-mm-yyyy',
  value = '',
  hasError,
  errorLabel,
  icon,
  required,
  disabled,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const selectedDate = useMemo(() => parseISODate(value), [value]);
  const displayValue = useMemo(
    () => (value ? formatDisplayDate(value) : ''),
    [value],
  );

  const emitChange = useCallback(
    (isoValue: string) => {
      if (!onChange) return;
      const syntheticEvent = {
        target: {
          name,
          value: isoValue,
          type: 'date',
          checked: false,
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    },
    [onChange, name],
  );

  const handleSelect = useCallback(
    (date: Date | undefined) => {
      if (date) {
        emitChange(toISODate(date));
      }
      setIsOpen(false);
    },
    [emitChange],
  );

  const handleToggle = useCallback(() => {
    if (!disabled) setIsOpen((prev) => !prev);
  }, [disabled]);

  const handleClose = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen]);

  return (
    <DateInputContainer>
      {label && <Label htmlFor={id}>{label}</Label>}
      {hasError && <ErrorLabel id={`${id}-error`}>{errorLabel}</ErrorLabel>}
      <DateInputWrapper ref={wrapperRef}>
        {icon && <IconWrapper $isOpen={isOpen}>{icon}</IconWrapper>}
        <DateTrigger
          id={id}
          type="button"
          aria-describedby={hasError ? `${id}-error` : undefined}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          onClick={handleToggle}
          disabled={disabled}
          $hasError={hasError}
          $hasValue={!!value}
          $isOpen={isOpen}
        >
          {displayValue || placeholder}
        </DateTrigger>
        {isOpen && (
          <>
            <Overlay onClick={handleClose} />
            <CalendarDropdown role="dialog" aria-label="Calendar">
              <DayPicker
                mode="single"
                locale={nl}
                selected={selectedDate}
                onSelect={handleSelect}
                defaultMonth={selectedDate || new Date()}
                disabled={{ before: new Date() }}
                required={required}
              />
            </CalendarDropdown>
          </>
        )}
      </DateInputWrapper>
    </DateInputContainer>
  );
};
